import { Box, Tooltip } from "@mui/material";
import colors from "../../../../theme/Colours";
import {
  LargeBoldText,
  RegularText,
  SemiBoldText,
  StyledKpiCard,
} from "../styles";
import OutletDialog, {
  OutletRow,
} from "../../../../common/outletDialog/outletDialog";
import { useState, useCallback, useMemo } from "react";
import CustomDropdown from "../../../../common/CustomDropdown";
import {
  orderIntervalDropdownStyles,
  OrderIntervalDropdownWrapper,
} from "../styles";

interface PaymentIntervalValue {
  account_type: string;
  show_details: any[];
  customer_count?: number;
  interval?: string;
  total_amount?: number;
  freq: string;
}

interface PaymentIntervalDetail {
  period: string;
  name: string;
  resultValues?: PaymentIntervalValue[];
  values?: PaymentIntervalValue[];
}

interface PaymentIntervalAnalysis {
  key: string;
  name: string;
  unit: string;
  details: PaymentIntervalDetail[];
  isPeriodOneDataExists: boolean;
  isPeriodTwoDataExists: boolean;
}

interface PaymentActivityDetailsProps {
  paymentIntervalAnalysis?: PaymentIntervalAnalysis;
}

interface Option {
  value: string;
  label: string;
}

const normalizeType = (raw: string) => {
  const s = (raw || "").toLowerCase().replace(/\s+/g, "");
  if (s.startsWith("active")) return "active";
  if (s.startsWith("lapsed")) return "lapsed";
  if (s.includes("inactive") || s.includes("in-active") || s.startsWith("inac"))
    return "inactive";
  return "other";
};

const cleanLabel = (raw: string) => {
  const norm = normalizeType(raw);
  if (norm === "active") return "Active";
  if (norm === "lapsed") return "Lapsed";
  if (norm === "inactive") return "Inactive";
  return raw;
};

const formatINR = (v: unknown) => {
  const n =
    typeof v === "string" ? Number(v.replace(/,/g, "")) : Number(v ?? 0);
  if (Number.isNaN(n)) return "₹0";
  return `₹${n.toLocaleString("en-IN")}`;
};

export default function PaymentActivityDetails({
  paymentIntervalAnalysis,
}: PaymentActivityDetailsProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<string>("");
  const [selectedView, setSelectedView] = useState<string>("");

  // Get current period data handling both resultValues and values
  const getCurrentPeriodData = useCallback(() => {
    if (!paymentIntervalAnalysis?.details) return null;

    const currentPeriod = paymentIntervalAnalysis.details.find(
      (detail) => detail.period === "current"
    );

    if (!currentPeriod) return null;

    // Handle both resultValues and values for backward compatibility
    const data = currentPeriod.resultValues || currentPeriod.values || [];

    return data;
  }, [paymentIntervalAnalysis]);

  const getShowDetailsForAccountType = useCallback(
    (accountType: string): OutletRow[] => {
      const currentPeriodData = getCurrentPeriodData();
      if (!currentPeriodData) return [];

      const accountValue = currentPeriodData.find(
        (value) => value.account_type === accountType
      );

      const details = accountValue?.show_details ?? [];

      return details.map((detail: any) => ({
        outletName: (detail.client_name || "").toString().trim(),
        email: (detail.email || "").toString().trim(),
        outstandingAmount: detail.outstanding_amount,
      }));
    },
    [getCurrentPeriodData]
  );

  const handleAccountTypeClick = (accountType: string) => {
    setSelectedAccountType(accountType);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAccountType("");
    setSelectedView("");
  };

  const handleDropdownChange = (v: string) => {
    setSelectedView(v);
    setDialogOpen(true);
  };

  const dialogRows = selectedView
    ? getShowDetailsForAccountType(selectedView)
    : getShowDetailsForAccountType(selectedAccountType);

  const getDialogTitle = () => {
    const accountType = selectedView || selectedAccountType;
    if (!accountType) return "";

    switch (accountType) {
      case "Active":
        return "Active Accounts";
      case "Lapsed":
        return "Lapsed Accounts";
      case "Inactive":
        return "Inactive Accounts";
      default:
        return `${cleanLabel(accountType)} Accounts`;
    }
  };

  // Generate dropdown options from current period data
  const dropdownOptions = useMemo((): Option[] => {
    const currentPeriodData = getCurrentPeriodData();
    if (!currentPeriodData?.length) return [];

    return currentPeriodData.map((v: PaymentIntervalValue) => ({
      value: v.account_type,
      label: `${cleanLabel(v.account_type)} Accounts`,
    }));
  }, [getCurrentPeriodData]);

  // If no paymentIntervalAnalysis, this means we're not in finance-analytics mode
  // Render a placeholder to maintain layout stability
  if (!paymentIntervalAnalysis) {
    return (
      <StyledKpiCard
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "6px 0px",
          height: "100%",
          minHeight: "85px",
          visibility: "hidden",
        }}
      />
    );
  }

  // Extract payment activity numbers from payment interval analysis
  const getPaymentActivityNumbers = () => {
    const currentPeriodData = getCurrentPeriodData();
    if (!currentPeriodData) {
      return { active: 0, lapsed: 0, inactive: 0 };
    }

    const active =
      currentPeriodData.find((value) => {
        const norm = normalizeType(value.account_type);
        return norm === "active";
      })?.customer_count || 0;

    const lapsed =
      currentPeriodData.find((value) => {
        const norm = normalizeType(value.account_type);
        return norm === "lapsed";
      })?.customer_count || 0;

    const inactive =
      currentPeriodData.find((value) => {
        const norm = normalizeType(value.account_type);
        return norm === "inactive";
      })?.customer_count || 0;

    return {
      active: parseInt(String(active), 10) || 0,
      lapsed: parseInt(String(lapsed), 10) || 0,
      inactive: parseInt(String(inactive), 10) || 0,
    };
  };

  const { active, lapsed, inactive } = getPaymentActivityNumbers();

  return (
    <StyledKpiCard
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "6px 0px",
        height: "100%",
        minHeight: "85px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pb: 0.5,
        }}
      >
        <SemiBoldText
          color="#1a1a18"
          sx={{ fontSize: "14px", fontWeight: 600 }}
        >
          Payment Activity Details
        </SemiBoldText>
        {dropdownOptions.length > 0 && (
          <OrderIntervalDropdownWrapper>
            <CustomDropdown
              label="View"
              value=""
              options={dropdownOptions}
              onChange={handleDropdownChange}
              customStyles={orderIntervalDropdownStyles}
            />
          </OrderIntervalDropdownWrapper>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {/* Active Accounts */}
        <Tooltip title="Outstanding in last 30 days" arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1px 16px",
              margin: "0 10px 2px",
              borderRadius: "8px",
              background: "#e6f1fb",
              border: "1px solid #b5d4f4",
              cursor: "default",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <RegularText
                sx={{ fontSize: "11px", fontWeight: 600, color: "#0C447C" }}
              >
                Active Accounts
              </RegularText>
            </Box>
            <LargeBoldText
              sx={{ fontSize: "18px", fontWeight: 700, color: "#185FA5" }}
            >
              {active}
            </LargeBoldText>
          </Box>
        </Tooltip>

        {/* Lapsed Accounts */}
        <Tooltip title="Outstanding amount since last 30-90 days" arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1px 16px",
              margin: "0 10px 2px",
              borderRadius: "8px",
              background: "#d6f0e8",
              border: "1px solid #9FE1CB",
              cursor: "default",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <RegularText
                sx={{ fontSize: "11px", fontWeight: 600, color: "#085041" }}
              >
                Lapsed Accounts
              </RegularText>
            </Box>
            <LargeBoldText
              sx={{ fontSize: "18px", fontWeight: 700, color: "#0F6E56" }}
            >
              {lapsed}
            </LargeBoldText>
          </Box>
        </Tooltip>

        {/* Inactive Accounts */}
        <Tooltip title="Outstanding amount in 90+ days" arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1px 16px",
              margin: "0 10px 4px",
              borderRadius: "8px",
              background: "#fde8e0",
              border: "1px solid #f5c4b3",
              cursor: "default",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <RegularText
                sx={{ fontSize: "11px", fontWeight: 600, color: "#712B13" }}
              >
                Inactive Accounts
              </RegularText>
            </Box>
            <LargeBoldText
              sx={{ fontSize: "18px", fontWeight: 700, color: "#993C1D" }}
            >
              {inactive}
            </LargeBoldText>
          </Box>
        </Tooltip>
      </Box>

      <OutletDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        rows={dialogRows}
        emptyMessage={
          selectedView || selectedAccountType
            ? `No ${cleanLabel(selectedView || selectedAccountType).toLowerCase()} accounts found`
            : "No accounts"
        }
        maxWidth="lg"
        showOutstanding={true}
        formatOutstanding={(v) => formatINR(v ?? 0)}
      />
    </StyledKpiCard>
  );
}
